// 확장자를 적는 이유: 이 파일은 단위 테스트가 앱 번들 밖(node)에서 그대로 읽는다 — node ESM 은 상대 경로의
// 확장자를 추론하지 않는다. webpack 은 확장자가 있어도 같은 파일을 찾는다.
import { ROUTE_LAYOUT, routeLayoutOf } from '../../utils/routeLayout.js'
import { HttpError, SYNTHETIC_ERROR_KEY } from './httpError.js'

/**
 * **세션(쿠키)으로 인증되는 쓰기에 싣는 CSRF 헤더의 이름 — 서버와의 계약 한 곳** (R-O X8). appserver `SessionCsrf.HEADER_NAME` 과
 * 같은 글자이고, 값은 로그인 응답의 `csrfToken`(스토어 `auth`)이다. 서버는 이 이름 하나만 받는다 — 다른 이름(세션 저장소의 기본
 * 이름 등)은 토큰 없음과 같아, 서버가 검사를 켜면(`SESSION_CSRF_ENFORCE`) 그 쓰기가 403 이다.
 *
 * 싣는 자리는 셋이고 모두 [csrfHeaders]·이 상수를 지난다: 공통 요청 인터셉터(`interceptors.js`) · axios 밖의 직접 업로드(`el-upload`
 * 의 `:headers` — 스토어 게터 `auth/csrfHeaders`) · 오케스트레이터 호출(`utils/orchestrator.js`).
 */
export const CSRF_HEADER = 'X-XSRF-TOKEN'

/**
 * 토큰 → 실을 헤더([CSRF_HEADER] 하나). 토큰이 없으면(로그인 전) 아무것도 싣지 않는다 — 빈 값을 보내지 않는다. 순수 — 돌려준 값은
 * 얼어 있다(받은 쪽이 고쳐 쓰지 못한다).
 *
 * @param {?string} token 로그인 응답의 `csrfToken`
 * @returns {Object<string, string>}
 */
export function csrfHeaders(token) {
  return Object.freeze(token ? { [CSRF_HEADER]: token } : {})
}

/**
 * 요청 config 에 찍는 스냅샷 키 — 그 요청을 보낸 시점의 라우트 레이아웃 (12R P10).
 *
 * 인터셉터가 **요청 시점**에 `config[REQUEST_ROUTE_LAYOUT] = routeLayoutOf(확정된 라우트)` 를 찍고, 403 판정은 그 불변
 * 값만 읽는다. 응답 시점의 전역 라우트를 읽으면 응답이 라우트 전환을 가로지를 때 다른 요청자로 판정된다.
 */
export const REQUEST_ROUTE_LAYOUT = 'routeLayout'

/** 요청을 보낸 시점의 라우트 레이아웃 — 스냅샷이 없으면(라우트 확정 전의 요청) 기본 레이아웃으로 본다 */
export function requestRouteLayout(config) {
  return (config && config[REQUEST_ROUTE_LAYOUT]) || ROUTE_LAYOUT.DEFAULT
}

/** 확정된 라우트 → 요청에 찍을 스냅샷 값. 순수 — 인터셉터(경계)가 config 에 넣는다 */
export function routeLayoutSnapshot(route) {
  return routeLayoutOf(route)
}

/**
 * **이 시도에 찍을 라우트 레이아웃** — 첫 시도는 지금 확정된 라우트, 재시도는 첫 시도의 스냅샷이다.
 *
 * 재시도는 **같은 요청**이다(요청자가 같다). 재시도 대기 중에 화면이 옮겨 가도 403 판정([logsOutOn403])은 그 요청을
 * 보낸 화면을 기준으로 한다 — 12R P10 이 응답 시점의 전역 라우트를 버린 것과 같은 이유다.
 */
export function stampedRouteLayout(config, route) {
  return retryAttemptOf(config) > 0 ? requestRouteLayout(config) : routeLayoutSnapshot(route)
}

/**
 * **403 이 세션을 끝내야 하는가 — 두 축을 한 곳에서 접는다** (SSOT).
 *
 * 이 앱의 403 은 기본적으로 세션을 지우고 `/login` 으로 하드 이동한다(`index.js`). 그 기본값에 두 예외가 있다:
 *
 * 1. **요청 축** — `config.logoutOn403 === false` (규약 §16.170). 그 요청의 403 에 화면이 이미 대체 동작을
 *    갖고 있다는 선언이다. 호출부가 붙인다.
 * 2. **화면 축** — 요청을 보낸 화면이 **임베드 레이아웃**(POS 안의 창)이면 어떤 403 도 세션을 끝내지 않는다. 그
 *    창에는 로그인 화면이 없다 — 로그아웃 뒤 `/login` 으로 하드 이동하면 사장님은 POS 안에서 어드민 로그인 폼을
 *    만난다. 그 화면은 403 을 페이지 안 안내로 답한다(결정 브리프 D2).
 *
 * 화면 축을 호출부마다 `logoutOn403:false` 로 적는 방식을 쓰지 않는 이유: 한 곳만 빠져도 그 화면이 통째로 튕긴다
 * (§16.170 이 겪은 결함의 재판). 라우트가 곧 선언이므로 판정 자리도 하나다.
 *
 * ★ **판정은 요청 시점의 스냅샷만 읽는다** (12R P10 · 원장 `ROUTE-STATE-BEFORE-NAV-SETTLES`). 종전에는 응답 시점의
 * `state.route` 를 읽었는데, 그 값은 `beforeEach` 에서 갱신돼 청크 실패로 내비게이션이 중단되면 저장 라우트(임베드)와
 * 실제 라우트(일반)가 갈렸고, 응답이 라우트 전환을 가로지르면 다른 요청자로 판정됐다. 이제 라우트는 `afterEach`
 * (확정된 라우트)에서만 커밋되고, 인터셉터가 요청마다 [REQUEST_ROUTE_LAYOUT] 을 찍으며, 여기서는 그것만 읽는다 —
 * 전역 상태를 읽는 인자가 없다.
 *
 * @param {?object} config 그 요청의 axios config — `logoutOn403` · [REQUEST_ROUTE_LAYOUT]
 */
export function logsOutOn403(config) {
  if (config && config.logoutOn403 === false) return false
  return requestRouteLayout(config) !== ROUTE_LAYOUT.EMBED
}

/**
 * **이 요청의 실패를 인터셉터가 토스트로 말하는가** — `showErrorMessage:false` 옵트아웃(규약 §16.175). 4xx 갈래와 전송 오류
 * 갈래가 같은 술어를 본다 (14R P58 — 종전에는 무응답 갈래가 옵트아웃을 안 봐 폴링 화면이 오프라인·타임아웃마다 빨간
 * 토스트를 띄웠다). 옵트아웃은 호출부가 대체 동작을 가질 때만이다 — 전송 오류의 대체 동작(인라인 안내)도 그 호출부 몫이다.
 */
export function showsErrorToast(config) {
  return !(config && config.showErrorMessage === false)
}

/**
 * **이 요청이 전역 로딩 카운트를 올리고 내리는가** — `showLoading:false` 옵트아웃. 요청·성공·실패 세 갈래가 같은 술어를 본다.
 *
 * 종전에는 실패 갈래만 옵트아웃을 안 보고 언제나 카운트를 내렸다 — 올린 적 없는 요청이 남의 스피너를 깎았다. 재시도는
 * 시도마다 올리고 내리므로 그 어긋남이 시도 수만큼 커진다. 시도 하나 = 올림 하나 + 내림 하나가 이 술어로 맞는다.
 */
export function showsLoading(config) {
  return !(config && config.showLoading === false)
}

/**
 * **전송 오류인가** — 응답이 없는 실패(오프라인·타임아웃·연결 끊김). 인터셉터는 응답이 있으면 상태를 실은 `HttpError` 로
 * 바꿔 던지므로, 호출부에 닿는 실패 중 응답 없는 axios 오류가 전송 오류다 (14R P58). 화면의 인라인 안내가 이 술어로 가른다.
 */
export function isTransportError(error) {
  return !!error && error.isAxiosError === true && error.response == null
}

// ─── 재시도 (R-K 네트워크 처리, 2026-09-24) ───────────────────────────────────────────────────────────────

/**
 * **앱 axios 인스턴스의 옵션**(`baseURL` 밖의 전부) — `index.js` 가 인스턴스를 만들 때와 단위 시험이 하네스를 만들 때 **같은
 * 값**을 쓴다(리뷰 L4). 시험이 따로 적으면 앱의 설정이 빠져도 시험은 모른다.
 *
 * - `timeout` 60초 — 긴 조회(엑셀·통계)에 맞춘 기본값. 작은 부가 조회는 [LOOKUP_REQUEST_CONFIG] 가 줄인다.
 * - `transitional.clarifyTimeoutError` — 타임아웃을 `ETIMEDOUT` 으로 낸다. 기본값이면 타임아웃과 중단(페이지 이탈)이 같은
 *   `ECONNABORTED` 라 재시도 판정([transportFailureOf])이 둘을 가를 수 없고, 가벼운 조회의 타임아웃 재시도가 조용히 꺼진다.
 * - `validateStatus` 2xx 만 — 그 밖은 axios 가 거부해 인터셉터가 사람에게 말한다.
 * - `withCredentials` — 세션 쿠키(어드민과 API 는 출처가 다르다).
 */
export const API_CLIENT_OPTIONS = Object.freeze({
  timeout: 60 * 1000,
  transitional: Object.freeze({ clarifyTimeoutError: true }),
  validateStatus: (status) => status >= 200 && status < 300,
  withCredentials: true,
})

/**
 * **재시도 지연 표** — 인덱스가 시도 번호(0 = 첫 시도의 실패), 값이 다음 시도까지의 대기(ms). 재시도 횟수는 이 표의 길이다
 * (따로 적지 않는다). 2회 · 300ms → 1s: 파드 교체·포트 포워딩이 한순간 끊긴 연결은 1초 안팎에 돌아오고, 그보다 길면 사람에게
 * 말하는 편이 낫다(끝내 실패하면 토스트는 그때 한 번).
 */
export const RETRY_DELAYS_MS = Object.freeze([300, 1000])

/** 요청 config 에 싣는 시도 번호 키 — 없으면 0(첫 시도). 인터셉터가 재요청 config 에만 싣는다([nextAttemptConfig]) */
export const REQUEST_RETRY_ATTEMPT = 'retryAttempt'

/** 요청 config 에 싣는 「이 시도를 보낸 시각」(ms) 키 — 요청 인터셉터(경계)가 시도마다 주입된 시계로 찍는다 */
export const REQUEST_SENT_AT = 'sentAt'

/**
 * **「빨리 끝난」 연결 실패의 상한**(ms) — 보낸 뒤 이 안에 난 연결 실패만 다시 보낸다(리뷰 M2).
 *
 * 브라우저가 연결 실패(`ERR_NETWORK`)로 보는 것은 둘이다: ① 연결 순간의 실패(거부·끊김·포트 포워딩 불안정·백엔드 없음 —
 * 요청이 핸들러에 닿지 않았을 가능성이 높다), ② **로드밸런서가 만든 응답**(GCLB 502 등). 어드민과 API 는 출처가 달라
 * 응답에 CORS 헤더가 있어야 브라우저가 보여 주는데, 로드밸런서가 직접 만든 오류에는 그 헤더가 없다(BackendConfig 에
 * `customResponseHeaders` 없음) — 그래서 무거운 조회 중 파드가 죽어 난 502 도 연결 실패로 보인다. ②를 다시 보내면 같은
 * 부하가 다른 파드로 간다.
 *
 * 둘을 가르는 것은 걸린 시간이다. 연결 수립(TCP·TLS)은 느린 망에서도 1초 안팎이고, 평소 조회는 서버에서 0.5초 안에 끝난다
 * (dev 로그인 화면 조회 최대 466ms, `/language` 0.28s·언어 데이터 0.4~0.9s 실측). 2초를 넘겨 끊긴 연결은 서버가 요청을
 * 붙잡고 있었다는 뜻이라 다시 보내지 않는다. 가벼운 조회([isLookupRequest])는 예외다 — 작고 가벼워 느린 실패도
 * 다시 보낸다(타임아웃을 다시 보내는 것과 같은 이유).
 */
export const FAST_FAILURE_MS = 2 * 1000

/** 가벼운 조회의 타임아웃 — 서버 처리는 dev 72시간 최대 466ms 였다. 그 스무 배를 넘으면 전송 구간이 멈춘 것이다 */
export const LOOKUP_TIMEOUT_MS = 10 * 1000

/**
 * **가벼운 조회 프로필** — 부팅·로그인 화면·셀렉트 옵션처럼 작고 화면 본체가 아닌 조회가 쓴다. 이 한 값이 네 가지를 함께 말한다:
 *
 * - 짧은 타임아웃 + 느린 실패(타임아웃·늦은 연결 끊김)도 재시도(`lookup` 옵트인 — 판정은 [isLookupRequest]). 기본
 *   타임아웃(60초, 엑셀·통계 같은 긴 조회)은 그대로다 —
 *   무거운 조회를 60초 뒤 한 번 더 부르면 서버 부하만 두 배라, 타임아웃 재시도는 이 프로필을 쓰는 조회에만 연다.
 * - **전역 잠금 로딩을 켜지 않는다**(`showLoading:false`) — 화면 본체가 아닌 조회가 멈춘 전송 구간(최대 10초 × 3회)
 *   동안 「처리중 입니다...」 마스크로 화면을 덮으면 로그인 폼조차 쓸 수 없다(리뷰 M1).
 * - **실패 토스트를 띄우지 않는다**(`showErrorMessage:false`) — 부가 조회라 호출부가 대체 동작을 갖는다(언어 목록 → 지금
 *   로케일 한 줄, 언어 데이터 → 폴백 로케일 메시지). 이 프로필을 쓴다는 것은 「실패하면 조용히 대신 답한다」는 선언이다 —
 *   대체 동작이 없는 조회는 쓰지 않는다(§16.175 와 같은 규칙).
 *
 * 호출부마다 옵트아웃을 덧대지 않는다 — 판정은 이 값 한 곳이다.
 */
export const LOOKUP_REQUEST_CONFIG = Object.freeze({
  timeout: LOOKUP_TIMEOUT_MS,
  lookup: true,
  showLoading: false,
  showErrorMessage: false,
})

/**
 * **업로드 요청의 시간 — 300초.** API 서버 앞 로드밸런서의 백엔드 한도(appserver `k8s-production-ingress.yaml` 의 API 서버
 * `BackendConfig.timeoutSec: 300`)와 같은 값이다. 기본 60초([API_CLIENT_OPTIONS])는 큰 파일을 읽고 줄을 저장하는 업로드(고객
 * 가져오기 — 서버가 읽기까지 그 요청 안에서 끝낸다)에 짧다. 넘으면 로드밸런서가 CORS 헤더 없는 504 로 끊어 전송 오류로 보인다 —
 * 서버는 끝냈을 수 있으므로 화면이 「가져온 기록」으로 안내한다. 쓰기라 재시도는 없다([retryDelay]).
 */
export const UPLOAD_TIMEOUT_MS = 300 * 1000

/** 업로드 요청 config 한 곳 — 화면은 여기에 진행률(`onUploadProgress`)·옵트아웃만 얹는다 */
export const UPLOAD_REQUEST_CONFIG = Object.freeze({ timeout: UPLOAD_TIMEOUT_MS })

/** 전송 실패의 종류 — 이름이 있는 둘만 재시도 판정에 쓰인다 */
export const TRANSPORT_FAILURE = Object.freeze({ NETWORK: 'network', TIMEOUT: 'timeout' })

/** 다시 보내도 서버 상태가 같은 메서드 — 쓰기는 요청이 서버에 닿았는지 모르므로 절대 다시 보내지 않는다 */
const IDEMPOTENT_METHODS = Object.freeze(['get', 'head', 'options'])

/**
 * **응답 없는 실패의 종류** — 연결 실패(`ERR_NETWORK`: 거부·끊김)와 타임아웃(`ETIMEDOUT`)만 이름이 있다.
 *
 * 타임아웃이 `ETIMEDOUT` 으로 오는 것은 인스턴스의 `transitional.clarifyTimeoutError` 덕이다([API_CLIENT_OPTIONS]) — 그 설정이 없으면
 * axios 는 타임아웃과 중단(페이지 이탈 등)을 같은 `ECONNABORTED` 로 낸다. 중단·취소(`ECONNABORTED`·`ERR_CANCELED`)는 호출자나
 * 브라우저가 끊은 요청이라 이름이 없다(`null`) — 다시 보낼 이유가 없다.
 */
export function transportFailureOf(error) {
  if (!isTransportError(error)) return null
  if (error.code === 'ERR_NETWORK') return TRANSPORT_FAILURE.NETWORK
  if (error.code === 'ETIMEDOUT') return TRANSPORT_FAILURE.TIMEOUT
  return null
}

/**
 * **가벼운 조회인가** — 옵트인(`lookup:true`)하고 **타임아웃이 [LOOKUP_TIMEOUT_MS] 이하**(axios 의 0 = 무제한은 아니다). 가벼운
 * 조회만 느린 실패(타임아웃·늦은 연결 끊김)를 다시 보낸다.
 *
 * 옵트인 하나로 판정하지 않는 이유: 프로필에 긴 타임아웃을 펼쳐 섞으면(`{ ...LOOKUP_REQUEST_CONFIG, timeout: 60_000 }`)
 * 「60초 × 3회」 약 3분이 된다. 두 설정이 따로 놀지 않게 병합 규칙을 여기 한 곳에 둔다(리뷰 L5) — 긴 타임아웃을 섞은
 * 요청은 기본 규칙(빨리 끝난 연결 실패만)으로 돌아간다. 순수.
 *
 * 빠지는 것은 **재시도 특권뿐**이다 — 펼쳐 섞은 설정에 `showLoading:false`·`showErrorMessage:false` 는 그대로 남아, 그 요청은
 * 60초 동안 잠금 로딩도 실패 토스트도 없이 조용하다. 프로필이 「실패하면 조용히 대신 답한다」는 선언이므로 일관되지만, 긴
 * 조회에 이 프로필을 섞어 쓰는 것은 그 침묵까지 받아들이는 일이다 — 긴 조회에는 쓰지 않는다(확인 리뷰 nit).
 */
export function isLookupRequest(config) {
  if (!config || config.lookup !== true) return false
  const timeout = config.timeout
  return typeof timeout === 'number' && timeout > 0 && timeout <= LOOKUP_TIMEOUT_MS
}

/** 그 config 의 시도 번호 — 첫 시도는 0 */
export function retryAttemptOf(config) {
  return (config && config[REQUEST_RETRY_ATTEMPT]) || 0
}

/**
 * 그 시도가 **빨리** 실패했는가 — 보낸 시각([REQUEST_SENT_AT])부터 `now` 까지가 [FAST_FAILURE_MS] 이하. 보낸 시각을 모르면
 * 빨랐다고 증명할 수 없으므로 거짓이다. 순수(시계는 인자)
 */
export function failedFast(config, now) {
  const sentAt = config && config[REQUEST_SENT_AT]
  if (typeof sentAt !== 'number' || typeof now !== 'number') return false
  const elapsed = now - sentAt
  return elapsed >= 0 && elapsed <= FAST_FAILURE_MS
}

/**
 * **이 실패를 다시 보내는가, 보낸다면 얼마 뒤에** — 재시도 규칙 한 곳(SSOT). 순수 — 대기와 재요청은 인터셉터(경계)가 한다.
 *
 * 1. 옵트아웃(`retry:false`)이면 보내지 않는다 — 폴링처럼 호출부가 스스로 다시 부르는 요청이 쓴다.
 * 2. 멱등 메서드(GET·HEAD·OPTIONS)만. **재시도는 GET 이 멱등이라는 전제에 선다** — 서버 상태를 바꾸는 GET 을 만들지 않는다.
 * 3. **브라우저가 볼 수 있는 응답은 다시 보내지 않는다.** 앱이 낸 4xx·5xx 는 CORS 헤더가 붙어 응답으로 보이고, 판정은 서버
 *    몫이다 — appserver 는 503 을 업무 답(자격증명 미설정·SQL 미적용·외부 연동 일시 불가)으로, 502 를 Firestore 프록시 실패로
 *    직접 낸다. 반면 **로드밸런서가 만든 오류(GCLB 502 등)는 CORS 헤더가 없어 연결 실패로 보인다** — 아래 4 가 다룬다. GCLB 의
 *    504 는 백엔드 300초 초과인데 클라이언트 타임아웃(60초)이 먼저 끊으므로 브라우저에 닿지 않는다.
 * 4. 연결 실패(`ERR_NETWORK`)는 **빨리 끝난 것만**([failedFast] — 보낸 뒤 [FAST_FAILURE_MS] 안). 요청이 핸들러에 닿지 않았을
 *    가능성이 높은 연결 순간의 실패다. 오래 붙잡혔다 끊긴 것(무거운 조회 중 파드가 죽어 난 로드밸런서 502)은 다시 보내지
 *    않는다. 가벼운 조회는 느린 연결 실패도 다시 보낸다.
 * 5. 타임아웃은 가벼운 조회([isLookupRequest] — [LOOKUP_REQUEST_CONFIG])일 때만.
 * 6. 지연은 [RETRY_DELAYS_MS] 의 그 시도 번호 칸 — 표가 끝나면 `null`.
 *
 * @param {*} error axios 오류 — `config`(메서드·옵트아웃·시도 번호·보낸 시각)는 그 안의 것을 읽는다
 * @param {number} now 지금 시각(ms) — 시계는 경계(인터셉터)가 준다
 * @returns {?number} 다음 시도까지의 대기(ms), 다시 보내지 않으면 `null`
 */
export function retryDelay(error, now) {
  const config = error && error.config
  if (!config || config.retry === false) return null
  if (!IDEMPOTENT_METHODS.includes(String(config.method || '').toLowerCase())) return null

  const failure = transportFailureOf(error)
  const lookup = isLookupRequest(config)
  const retryable =
    (failure === TRANSPORT_FAILURE.NETWORK && (lookup || failedFast(config, now))) ||
    (failure === TRANSPORT_FAILURE.TIMEOUT && lookup)
  if (!retryable) return null

  const delay = RETRY_DELAYS_MS[retryAttemptOf(config)]
  return delay === undefined ? null : delay
}

/** 같은 요청의 다음 시도 config — 시도 번호만 하나 올린다. 순수(원래 config 는 그대로) */
export function nextAttemptConfig(config) {
  return Object.assign({}, config, { [REQUEST_RETRY_ATTEMPT]: retryAttemptOf(config) + 1 })
}

/** 인터셉터가 변환한 HttpError와 원래 Axios 오류의 HTTP 상태를 같은 경로로 읽는다. */
export function httpStatusOf(error) {
  return (error && (error.status || (error.response && error.response.status))) || null
}

/**
 * **그 실패의 응답 본문** — `HttpError`(`data`)와 원래 Axios 오류(`response.data`)를 같은 경로로 읽는다.
 *
 * 문장으로 접히지 않는 사실이 있다: 형제 매장 후보·엑셀 오류 행·참여 조건 사유. 그것을 읽는 자리를
 * 화면마다 다르게 적으면 한쪽만 고쳐질 때 조용히 갈린다([httpStatusOf] 와 같은 이유로 한 곳이다).
 * 본문이 객체가 아니면(문자열 오류 페이지 등) `null` 이다 — 호출부는 「없다」 하나만 다룬다.
 */
export function httpErrorBody(error) {
  const body = (error && (error.data != null ? error.data : error.response && error.response.data)) || null

  return body && typeof body === 'object' ? body : null
}

/**
 * **거절 봉투의 칸별 문장** — appserver 의 새 거절 본문 `{reason, message, fields: [{field, message}]}`(전화주문 회차의 고객 가져오기·
 * 요청 문구·주소록 API — 계약 I1·core §b-0)에서 칸 이름 → 문장. 인터셉터가 폼에 앉히는 칸 오류는 옛 모양(`errorsMessage`)뿐이라
 * 이 모양의 칸 오류는 화면이 그 칸 아래에 직접 앉힌다. 읽는 자리를 화면마다 적지 않게 [httpErrorBody] 옆 한 곳에 둔다. 순수.
 *
 * @param {?object} body 실패 응답 본문([httpErrorBody])
 * @returns {Object<string, string>} 칸 이름 → 문장(문장이 없으면 빈 글자, 칸이 없으면 빈 객체)
 */
export function rejectionFieldsOf(body) {
  const fields = body && Array.isArray(body.fields) ? body.fields : []

  return fields
    .filter((item) => item && typeof item.field === 'string' && item.field)
    .reduce(
      (messages, item) =>
        Object.assign(messages, { [item.field]: typeof item.message === 'string' ? item.message.trim() : '' }),
      {}
    )
}

/**
 * 서버의 행동 안내를 보존한다. 저장 여부의 확실성은 이 문장의 존재로 판정하지 않는다.
 *
 * **전송 오류는 맨 앞에서 갈린다** — 응답 없는 실패([isTransportError])의 `error.message` 는 axios 의 영문
 * 내부 문장(`Network Error`·`timeout of 0ms exceeded`)이라 사람에게 할 말이 아니고, 인터셉터가 그 갈래를
 * `HttpError` 로 바꾸지 않아 이 함수까지 그대로 올라온다. 종전에는 그것이 그대로 화면 문장이 됐고
 * (`showErrorMessage:false` 로 토스트를 옵트아웃한 화면에서는 **그 영문 한 줄이 유일한 안내**였다),
 * 호출부가 넘긴 한국어 폴백은 가장 필요한 그 경우에 쓰이지 않았다.
 *
 * 판정과 문장이 같은 모듈에 있으므로([NETWORK_ERROR_MESSAGE] — 인터셉터의 토스트가 쓰는 같은 상수)
 * 호출부마다 `isTransportError` 분기를 덧대지 않는다.
 */
export function httpErrorMessage(error, fallback = '요청을 처리하지 못했습니다.') {
  if (isTransportError(error)) return NETWORK_ERROR_MESSAGE
  const data = error && error.response && error.response.data
  if (data && typeof data.message === 'string' && data.message.trim()) return data.message
  if (error && typeof error.message === 'string' && error.message.trim()) return error.message
  return fallback
}

/**
 * **저장 실패를 폼에 앉히는 법** — 필드 오류는 그 칸에(`fieldErrors` — 호출부가 `mergeErrors` 로 폼 오류 칸에 머지한다), 칸에
 * 앉지 못한 것만 폼 아래 한 줄로(`message`). 같은 문장을 두 번 말하지 않는다. 순수.
 *
 * 서버 400 은 필드 오류(`errorsMessage[]`)를 싣고 `HttpError.message` 는 그 문장들을 이어 붙인 것이라, 칸에 머지하고
 * [httpErrorMessage] 까지 한 줄로 그리면 같은 문장이 칸 아래와 폼 아래에 한 번씩 떴다(R-L 리뷰 L-5, 자동 취소 패널).
 *
 * - 칸이 있는 필드(`fieldKeys`)의 오류 → `fieldErrors`.
 * - 칸이 없는 필드의 오류 → 그 문장들만 한 줄로(칸이 없으면 한 줄이 유일한 자리다 — 버리지 않는다).
 * - 필드 오류가 없으면(메시지뿐인 400·404·409·전송 오류) → [httpErrorMessage] 한 줄.
 *
 * @param {*} error 저장 요청의 실패
 * @param {Array<string>} fieldKeys 이 폼이 칸으로 보여 주는 서버 필드 이름
 * @param {string} fallback 문장이 없는 실패의 폴백
 * @returns {{fieldErrors: Object<string, string>, message: string}}
 */
export function formFailureOf(error, fieldKeys, fallback) {
  const messages = (error instanceof HttpError && error.messages) || {}
  const fieldEntries = Object.keys(messages)
    .filter((key) => key !== SYNTHETIC_ERROR_KEY)
    .map((key) => [key, typeof messages[key] === 'string' ? messages[key].trim() : ''])
    .filter(([, text]) => !!text)

  if (fieldEntries.length === 0) return { fieldErrors: {}, message: httpErrorMessage(error, fallback) }

  const shown = fieldEntries.filter(([key]) => fieldKeys.includes(key))
  const unshown = fieldEntries.filter(([key]) => !fieldKeys.includes(key))

  return {
    fieldErrors: Object.fromEntries(shown),
    message: unshown.map(([, text]) => text).join(' | '),
  }
}

/** 전화번호 꼴 — 경로에 실린 10자리 이상 숫자. 매장 ID(`S`+9자리)·코드·순번은 그보다 짧다 */
const PHONE_LIKE_DIGITS = /\d{10,}/g

/** 로그에 남길 경로 — 질의 문자열(검색 조건 — 전화번호·이름)을 떼고 전화번호 꼴 숫자를 가린다 */
function loggablePath(url) {
  return String(url).split(/[?#]/)[0].replace(PHONE_LIKE_DIGITS, '***')
}

/**
 * **실패한 요청이 무엇이었나** — 방법과 가린 경로([loggablePath])만. 인터셉터(경계)가 응답 실패를 `HttpError` 로 바꿀 때
 * 이것을 싣는다(리뷰 N4 — 종전 `HttpError` 는 config 를 싣지 않아 실패 로그만으로는 어느 요청이었는지 몰랐다). 요청 본문·
 * 헤더·질의 문자열은 싣지 않는다 — `HttpError` 가 개인정보를 들고 다니지 않게 만들 때 가린다. 순수.
 *
 * @returns {?{method: ?string, url: ?string}}
 */
export function failedRequestOf(config) {
  if (!config) return null
  return {
    method: config.method ? String(config.method).toUpperCase() : null,
    url: config.url ? loggablePath(config.url) : null,
  }
}

/** 서버 오류 코드 꼴(`C003`·`DISCOUNT_EXHAUSTED` 같은 토큰) — 문장·값이 코드 칸으로 새지 않게 이 꼴만 남긴다 */
const SERVER_CODE_TOKEN = /^[A-Za-z0-9_.:-]{1,64}$/

/** 응답 본문의 서버 오류 코드(`ErrorResponse.code`) — 토큰 꼴일 때만, 아니면 `null` */
function serverCodeOf(error) {
  const body = httpErrorBody(error)
  const code = body && body.code
  return typeof code === 'string' && SERVER_CODE_TOKEN.test(code) ? code : null
}

/**
 * **요청 실패인가** — axios 오류(무응답·타임아웃·중단, 인터셉터가 바꾸지 않은 것)거나 인터셉터가 응답 4xx·5xx 로 만든
 * `HttpError` 다. 그 밖(성공 콜백의 `TypeError` 등 코드 결함)은 요청 실패가 아니다. 순수.
 *
 * 두 자리가 **같은 구분**을 쓴다 — 실패 로그의 요약([loggableFailure])과 legacy 헬퍼의 계약(`legacyHelpers.js` — 요청
 * 실패는 값, 그 밖은 던짐). 따로 적으면 「요약해 찍었는데 던졌다」거나 그 반대로 갈린다.
 */
export function isRequestFailure(error) {
  return !!error && (error.isAxiosError === true || error instanceof HttpError)
}

/**
 * **실패 로그에 남길 값** — 요청 실패면 요약, 그 밖의 오류는 그대로(리뷰 L3). 순수 — 찍는 것은 `index.js`(경계)의
 * `logHttpFailure` 한 곳이다.
 *
 * axios 오류는 요청 config 를 통째로 싣는다: 본문(`data` — 쿠폰 발송이면 고객 번호·이름, 로그인이면 비밀번호), 헤더(CSRF
 * 토큰), 질의 문자열. 개발자 도구에서 펼치면 다 보이고 `toJSON` 도 그것을 직렬화한다. `HttpError` 는 응답 본문(`data`)을
 * 싣는다. 그래서 요청 실패는 **HTTP 상태·axios 코드·서버 오류 코드·방법·경로·문장**만 남긴다 — 방법·경로는 axios 오류면
 * config 에서, `HttpError` 면 인터셉터가 실은 [failedRequestOf] 에서(리뷰 N4). 서버 오류 코드는 응답 본문의 `code` 가 토큰
 * 꼴일 때만이다. 요청 실패가 아닌 오류(성공 콜백의 `TypeError` 등)는 요청 본문을 싣지 않으므로 그대로 둔다 — 스택을 잃으면
 * 결함을 못 찾는다.
 *
 * @returns {object|*} 요약(`{status, code, serverCode, method, url, message}`) 또는 그 오류 자체
 */
export function loggableFailure(error) {
  if (!isRequestFailure(error)) return error

  const request = error instanceof HttpError ? error.request : failedRequestOf(error.config)
  return {
    status: httpStatusOf(error),
    code: error.code || null,
    serverCode: serverCodeOf(error),
    method: (request && request.method) || null,
    url: (request && request.url) || null,
    message: typeof error.message === 'string' ? error.message : '',
  }
}

/** 전송 오류의 사용자 문장 — 인터셉터의 토스트와 옵트아웃한 화면의 인라인 안내가 같은 문장을 쓴다 (SSOT) */
export const NETWORK_ERROR_MESSAGE = '네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'

/**
 * **못 읽었다는 이유** — 권한 밖일 수 있는 조회는 두 갈래로만 막힌다.
 *
 * - [READ_DENIED.ACTION] — 세션에 그 액션이 없어 **부르지 않았다**. 보안 계층의 403 은 서버가 그 세션을
 *   끊어서 클라이언트 옵트아웃으로 되살릴 수 없다(compose 실측 2026-09-15: `GET /auth` 403 뒤
 *   `GET /account` 가 403). 그래서 이 갈래는 요청 자체를 보내지 않는다.
 * - [READ_DENIED.FORBIDDEN] — 보냈고 업무 계층이 403 으로 답했다(`AccountIssuePolicy` 같은 컨트롤러 안
 *   판정). 세션은 살아 있고 서버 문장이 곧 화면이 보여 줄 답이다.
 */
export const READ_DENIED = {
  ACTION: 'ACTION',
  FORBIDDEN: 'FORBIDDEN',
  /**
   * 보냈는데 403 이 아닌 이유로 못 읽었다(500·전송 오류). [permittedRead] 는 이 갈래를 **던진다** —
   * 「권한 밖」이 아니기 때문이다. 던진 것을 받아 화면 상태로 끝내는 호출부가 이 낱말로 답한다:
   * 「못 읽었다」는 같지만 이유가 다르므로 [FORBIDDEN] 의 문구(권한 안내)를 빌려 쓰지 않는다.
   */
  FAILED: 'FAILED',
}

/**
 * 읽었다 — 조회 결과를 값으로 감싼다. 순수.
 *
 * 「못 읽었다」를 예외가 아니라 값으로 돌려주는 이유: 403 의 대체 동작(안내하고 그 자리를 그대로 둔다)은
 * 호출부마다 다르고, 예외로 올리면 호출부가 `try/catch` 안에서 상태 코드를 다시 세게 된다 — 규칙이 화면
 * 수만큼 복제된다(§16.170·§16.175 가 겪은 자리).
 */
export function allowedRead(data) {
  return { allowed: true, data, reason: null, message: '' }
}

/** 못 읽었다 — 이유와 (있으면) 서버 문장. 순수 */
export function deniedRead(reason, message) {
  return { allowed: false, data: null, reason, message: typeof message === 'string' ? message : '' }
}

/**
 * legacy 헬퍼(`mainVue.$axios.*`)의 **결과 값** — 요청이 성공했다. `data` 는 성공 콜백이 받은 것과 같다. 순수.
 *
 * 헬퍼를 기다려 이어 가는 호출부(`await` 뒤 성공 안내·다음 조회)는 이 값의 `ok` 로 갈래를 나눈다 — 「던져지지 않았으니
 * 성공」으로 읽지 않는다(R-K 보고서 §11 ①).
 */
export function requestSucceeded(data) {
  return { ok: true, data }
}

/**
 * legacy 헬퍼의 **결과 값** — 요청이 실패했다([isRequestFailure]). `error` 는 `HttpError`(`status`·`data`·`messages`)거나
 * 무응답 axios 오류다. 사람에게 할 말은 인터셉터가 이미 했다(토스트 옵트아웃이면 호출부가 이 값으로 말한다). 순수.
 */
export function requestFailed(error) {
  return { ok: false, error }
}
